import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  Button,
  IconButton,
  CircularProgress,
  Tooltip,
  Popover,
  TextField,
  Menu,
} from '@mui/material';

import SimulationSettingsModal from './SimulationSettingsModal';
import LiftangoInsightsCarousel from './LiftangoInsightsCarousel';
import {
  MetricsDistributionChart,
  buildTripDistanceDistribution,
  buildWaitTimeDistribution,
  type QuartileDistribution,
} from './MetricsDistributionChart';
import { SimulationRunSummary } from '../../../../types/simulation';
import { liftangoBrandTokens } from '../../../../theme';
import { trackAnalyticsEvent } from '../../../../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../../../../services/eventTaxonomy';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../../utils/iconFallbacks';
export interface SimulationResultsSidebarMetric {
  label: string;
  field: string;
  unit: string;
}

export interface SimulationResultsSidebarProps {
  selectedRunId: string | null;
  onSelectedRunIdChange: (runId: string) => void;
  simulationResults: SimulationRunSummary[];
  loadingResults: boolean;
  resultsError: string | null;
  onReload: () => void;
  selectedSim: SimulationRunSummary | null;
  metrics: SimulationResultsSidebarMetric[];
  onBackToMap: () => void;
  onShare?: () => void;
  /** Current total height of the bottom AnalyticsDeck so the sidebar stops above it. */
  deckTotalHeight?: number;
  /** Show the Liftango Insights carousel in the sidebar. */
  showInsights?: boolean;
  onAddToMapOnDemand?: () => void;
  onAddToMapFixedRoute?: () => void;
  addToMapOnDemandDisabled?: boolean;
  addToMapFixedRouteDisabled?: boolean;
  addToMapBusy?: boolean;
}

/** Liftango brand palette */
const BRAND = {
  primaryOrange: liftangoBrandTokens.palette.primary,
  tarmacBlack: liftangoBrandTokens.palette.textPrimary,
  successGreen: liftangoBrandTokens.palette.success,
  teal: liftangoBrandTokens.palette.secondary,
  labelGray: liftangoBrandTokens.palette.textSecondary,
  borderLight: liftangoBrandTokens.simulationResults.tooltipBorder,
} as const;

/** Sidebar on dark background (#3A3A48) – text and borders for contrast */
const SIDEBAR = {
  background: liftangoBrandTokens.simulationResults.sidebarBackground,
  label: liftangoBrandTokens.simulationResults.sidebarMutedText,
  value: liftangoBrandTokens.simulationResults.sidebarText,
  border: liftangoBrandTokens.simulationResults.sidebarBorder,
} as const;

const FONT_FAMILY = '"Roboto", sans-serif';

/** Data formatting: numbers with thousands separators, 1 decimal place for decimals, consistent naming (Requests not Trips). */
export function formatMetric(value: unknown, type: 'integer' | 'decimal' | 'percent'): string {
  if (value === undefined || value === null) return '--';
  if (typeof value !== 'number') return String(value);
  switch (type) {
    case 'integer':
      return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0, minimumFractionDigits: 0 }).format(value);
    case 'decimal':
      return new Intl.NumberFormat('en-US', { maximumFractionDigits: 1, minimumFractionDigits: 1 }).format(value);
    case 'percent':
      return new Intl.NumberFormat('en-US', { maximumFractionDigits: 1, minimumFractionDigits: 1 }).format(value <= 1 ? value * 100 : value) + '%';
    default:
      return new Intl.NumberFormat('en-US', { maximumFractionDigits: 1, minimumFractionDigits: 0 }).format(value);
  }
}

const getRaw = (sim: SimulationRunSummary | null, field: string): unknown =>
  sim ? sim[field as keyof SimulationRunSummary] : null;

const num = (v: unknown): number => (typeof v === 'number' ? v : 0);

/** Shared MUI Tooltip chrome (matches prior sidebar tooltips). */
const METRIC_TOOLTIP_POPPER_SX = {
  '& .MuiTooltip-tooltip': {
    bgcolor: liftangoBrandTokens.simulationResults.tooltipBackground,
    color: BRAND.tarmacBlack,
    border: `1px solid ${BRAND.borderLight}`,
    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
    p: 1.5,
  },
} as const;

const dropdownTripCount = (sim: SimulationRunSummary): number | null => {
  if (typeof sim.no_rides === 'number' && Number.isFinite(sim.no_rides)) return sim.no_rides;

  const row = sim as unknown as Record<string, unknown>;
  const toNumber = (value: unknown): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;

  const directCandidates = [
    row.num_requests,
    row.numRequests,
    row.total_rides,
    row.totalRequests,
    row.total_requests,
    row.rides_count,
    row.number_of_rides,
    row.request_count,
    row.no_requests,
  ];
  for (const candidate of directCandidates) {
    const value = toNumber(candidate);
    if (value != null) return value;
  }

  const summary = row.summary;
  if (summary && typeof summary === 'object') {
    const s = summary as Record<string, unknown>;
    const summaryCandidates = [
      s.num_requests,
      s.numRequests,
      s.total_rides,
      s.totalRequests,
      s.total_requests,
      s.no_rides,
      s.rides_count,
      s.number_of_rides,
      s.request_count,
    ];
    for (const candidate of summaryCandidates) {
      const value = toNumber(candidate);
      if (value != null) return value;
    }
  }

  if (Array.isArray(sim.ride_points)) return sim.ride_points.length;
  return null;
};

/** Shared MUI Tooltip chrome (matches prior sidebar tooltips). */
const MetricTooltipHint: React.FC<{
  tooltip: string | React.ReactNode;
  ariaLabel: string;
  /** Hero chips use top placement; metric rows use left (default). */
  placement?: 'top' | 'left' | 'bottom' | 'right';
}> = ({ tooltip, ariaLabel, placement = 'left' }) => {
  const title =
    typeof tooltip === 'string' ? (
      <Typography
        component="span"
        sx={{ fontSize: '0.8125rem', lineHeight: 1.5, maxWidth: 320, display: 'block', fontFamily: FONT_FAMILY }}
      >
        {tooltip}
      </Typography>
    ) : (
      tooltip
    );
  return (
    <Tooltip
      title={title}
      placement={placement}
      arrow
      enterDelay={300}
      leaveDelay={0}
      slotProps={{ popper: { sx: METRIC_TOOLTIP_POPPER_SX } }}
    >
      <Box
        component="span"
        tabIndex={0}
        aria-label={ariaLabel}
        onClick={(e) => e.stopPropagation()}
        sx={{
          cursor: 'help',
          color: SIDEBAR.label,
          fontSize: '0.6875rem',
          fontWeight: 700,
          lineHeight: 1,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 16,
          height: 16,
          borderRadius: '50%',
          border: `1px solid ${SIDEBAR.border}`,
          flexShrink: 0,
          '&:focus-visible': { outline: `2px solid ${BRAND.primaryOrange}`, outlineOffset: 1 },
        }}
      >
        ?
      </Box>
    </Tooltip>
  );
};
const MetricGraphOpenControl: React.FC<{
  onOpen: (event: React.MouseEvent<HTMLElement>) => void;
  ariaLabel: string;
}> = ({ onOpen, ariaLabel }) => (
  <Box
    component="button"
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onOpen(e);
    }}
    onMouseEnter={(e) => {
      onOpen(e);
    }}
    aria-label={ariaLabel}
    sx={{
      cursor: 'pointer',
      color: SIDEBAR.label,
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1,
      userSelect: 'none',
      background: 'none',
      border: 'none',
      padding: 0,
      m: 0,
      font: 'inherit',
      display: 'inline-flex',
      alignItems: 'center',
      '&:hover': { color: SIDEBAR.value },
      '&:focus-visible': { outline: `2px solid ${BRAND.primaryOrange}`, outlineOffset: 2 },
    }}
  >
    &gt;
  </Box>
);

/** Reusable row: label (+ optional ? tooltip), value (+ optional &gt; opens box plot only). */
const MetricRow: React.FC<{
  label: string;
  value: string;
  tooltip?: string | React.ReactNode;
  valueColor?: string;
  onGraphOpen?: (event: React.MouseEvent<HTMLElement>) => void;
}> = ({ label, value, tooltip, valueColor, onGraphOpen }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 0.5,
      py: 1.25,
      borderBottom: `1px solid ${SIDEBAR.border}`,
      width: '100%',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
      <Typography variant="body2" sx={{ color: SIDEBAR.label, fontSize: '0.875rem', fontFamily: FONT_FAMILY }}>
        {label}
      </Typography>
      {tooltip ? <MetricTooltipHint tooltip={tooltip} ariaLabel={`${label}: definition`} /> : null}
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
      <Typography
        variant="body2"
        sx={{
          color: valueColor ?? SIDEBAR.value,
          fontWeight: 600,
          fontSize: '0.875rem',
          fontFamily: FONT_FAMILY,
        }}
      >
        {value}
      </Typography>
      {onGraphOpen ? (
        <MetricGraphOpenControl onOpen={onGraphOpen} ariaLabel={`${label}: view box plot`} />
      ) : null}
    </Box>
  </Box>
);

/** Hero metric: optional ? (tooltip) beside label; &gt; opens box plot only when graph handler provided. */
const HeroCell: React.FC<{
  icon: React.ReactNode;
  value: string;
  label: string;
  tooltip?: string;
  iconColor: string;
  onGraphOpen?: (event: React.MouseEvent<HTMLElement>) => void;
}> = ({ icon, value, label, tooltip, iconColor, onGraphOpen }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      flex: 1,
      minWidth: 0,
    }}
  >
    <Box sx={{ color: iconColor, mb: 0.75, '& .MuiSvgIcon-root': { fontSize: 20 } }}>{icon}</Box>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, width: '100%' }}>
      <Typography
        sx={{
          fontFamily: FONT_FAMILY,
          fontSize: '1.25rem',
          fontWeight: 800,
          color: SIDEBAR.value,
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>
      {onGraphOpen ? (
        <MetricGraphOpenControl onOpen={onGraphOpen} ariaLabel={`${label}: view box plot`} />
      ) : null}
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.35, mt: 0.5, flexWrap: 'wrap' }}>
      <Typography
        sx={{
          fontFamily: FONT_FAMILY,
          fontSize: '10px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: SIDEBAR.label,
        }}
      >
        {label}
      </Typography>
      {tooltip ? (
        <MetricTooltipHint tooltip={tooltip} ariaLabel={`${label}: definition`} placement="top" />
      ) : null}
    </Box>
  </Box>
);

/** Section with small uppercase header and optional bottom border. */
const Section: React.FC<{
  title: string;
  children: React.ReactNode;
  last?: boolean;
}> = ({ title, children, last }) => (
  <Box
    sx={{
      mb: last ? 0 : 2,
      pb: last ? 0.5 : 2,
      borderBottom: last ? 'none' : `1px solid ${SIDEBAR.border}`,
    }}
  >
    <Typography
      component="h3"
      sx={{
        fontFamily: FONT_FAMILY,
        fontSize: '0.6875rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        color: SIDEBAR.label,
        mb: 1.5,
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const SimulationResultsSidebar: React.FC<SimulationResultsSidebarProps> = ({
  selectedRunId,
  onSelectedRunIdChange,
  simulationResults,
  loadingResults,
  resultsError,
  onReload,
  selectedSim,
  onBackToMap,
  onShare,
  deckTotalHeight = 0,
  showInsights = true,
  onAddToMapOnDemand,
  onAddToMapFixedRoute,
  addToMapOnDemandDisabled = false,
  addToMapFixedRouteDisabled = false,
  addToMapBusy = false,
}) => {
  const { t } = useTranslation('simulation');
  const METRIC_DEFINITIONS = useMemo(
    () => ({
      successRate: t('results.sidebar.metricDefinitions.successRate'),
      utilization: t('results.sidebar.metricDefinitions.utilization'),
      activeKm: t('results.sidebar.metricDefinitions.activeKm'),
      idleKm: t('results.sidebar.metricDefinitions.idleKm'),
      waitTime: t('results.sidebar.metricDefinitions.waitTime'),
      sharedTripRate: t('results.sidebar.metricDefinitions.sharedTripRate'),
      avgVehicleHours: t('results.sidebar.metricDefinitions.avgVehicleHours'),
    }),
    [t]
  );
  const raw = (field: string) => getRaw(selectedSim, field);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [addToMapMenuAnchorEl, setAddToMapMenuAnchorEl] = useState<HTMLElement | null>(null);
  const addToMapDisabled = addToMapOnDemandDisabled && addToMapFixedRouteDisabled;
  const [selectorSearch, setSelectorSearch] = useState('');
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [metricDetail, setMetricDetail] = useState<{
    anchor: HTMLElement;
    data: QuartileDistribution;
    title: string;
    unit: string;
  } | null>(null);

  const waitDistribution = selectedSim ? buildWaitTimeDistribution(selectedSim) : null;
  const tripDistanceDistribution = selectedSim ? buildTripDistanceDistribution(selectedSim) : null;

  const openMetricDetail = useCallback(
    (anchor: HTMLElement, payload: { data: QuartileDistribution; title: string; unit: string }) => {
      setMetricDetail({ anchor, ...payload });
    },
    []
  );

  const closeMetricDetail = useCallback(() => setMetricDetail(null), []);

  const filteredSimulationResults = useMemo(() => {
    const query = selectorSearch.trim().toLowerCase();
    if (!query) return simulationResults;
    return simulationResults.filter((sim) => {
      const scenario = (sim.scenario_name ?? '').toLowerCase();
      const runId = sim.run_id.toLowerCase();
      return scenario.includes(query) || runId.includes(query);
    });
  }, [simulationResults, selectorSearch]);

  const handleRunChange = useCallback((runId: string) => {
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.simulation_run_selected, {
      run_id: runId,
      available_runs_count: simulationResults.length,
      timestamp: new Date().toISOString(),
    });
    onSelectedRunIdChange(runId);
  }, [simulationResults.length, onSelectedRunIdChange]);

  const handleReload = useCallback(() => {
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.simulation_results_reloaded, {
      run_id: selectedRunId,
      timestamp: new Date().toISOString(),
    });
    onReload();
  }, [selectedRunId, onReload]);

  const handleBackToMap = useCallback(() => {
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.back_to_map_clicked, {
      from_screen: 'simulation_results',
      timestamp: new Date().toISOString(),
    });
    onBackToMap();
  }, [onBackToMap]);

  return (
    <>
    <Box
      sx={{
        position: 'absolute',
        left: 16,
        top: 16,
        width: 320,
        height: `calc(100% - 32px - ${deckTotalHeight}px)`,
        zIndex: 10,
        backgroundColor: SIDEBAR.background,
        borderRadius: 2,
        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1), 0 0 0 1px rgba(148, 163, 184, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Scrollable content area */}
      <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
      {/* Run ID selector */}
      <Box sx={{ p: 3, borderBottom: `1px solid ${SIDEBAR.border}` }}>
        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
          <InputLabel id="sidebar-run-select-label" sx={{ color: SIDEBAR.label, fontFamily: FONT_FAMILY }}>
            {t('results.sidebar.selectSimulation')}
          </InputLabel>
          <Select
            labelId="sidebar-run-select-label"
            value={selectedRunId || ''}
            label={t('results.sidebar.selectSimulation')}
            onChange={(e) => handleRunChange(e.target.value as string)}
            open={selectorOpen}
            onOpen={() => setSelectorOpen(true)}
            onClose={() => {
              setSelectorOpen(false);
              setSelectorSearch('');
            }}
            MenuProps={{
              autoFocus: false,
              PaperProps: {
                sx: {
                  maxHeight: 420,
                },
              },
            }}
            renderValue={(v) => {
                const sim = simulationResults.find((s) => s.run_id === v);
                return sim?.scenario_name || (v as string) || t('results.sidebar.selectSimulation');
              }}
            disabled={loadingResults}
            sx={{
              color: SIDEBAR.value,
              fontFamily: FONT_FAMILY,
              borderRadius: 1,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: SIDEBAR.border },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND.primaryOrange },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND.primaryOrange, borderWidth: 2 },
            }}
          >
            {!loadingResults && (
              <ListSubheader
                sx={{
                  backgroundColor: '#FFFFFF',
                  borderBottom: `1px solid ${SIDEBAR.border}`,
                  py: 1,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                }}
              >
                <TextField
                  size="small"
                  autoFocus
                  placeholder={t('results.sidebar.searchPlaceholder')}
                  fullWidth
                  value={selectorSearch}
                  onChange={(event) => setSelectorSearch(event.target.value)}
                  onKeyDown={(event) => {
                    event.stopPropagation();
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      color: 'text.primary',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: 'text.secondary',
                      opacity: 0.95,
                    },
                  }}
                />
              </ListSubheader>
            )}
            {loadingResults && (
              <MenuItem disabled>
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={18} sx={{ color: BRAND.primaryOrange }} />
                  <Typography variant="body2" sx={{ color: SIDEBAR.value, fontFamily: FONT_FAMILY }}>{t('results.sidebar.loading')}</Typography>
                </Box>
              </MenuItem>
            )}
            {!loadingResults && simulationResults.length === 0 && !resultsError && (
              <MenuItem disabled>
                <Typography variant="body2" sx={{ color: SIDEBAR.label, fontFamily: FONT_FAMILY }}>{t('results.sidebar.noResults')}</Typography>
              </MenuItem>
            )}
            {!loadingResults && filteredSimulationResults.length === 0 && (
              <MenuItem disabled>
                <Typography variant="body2" sx={{ color: SIDEBAR.label, fontFamily: FONT_FAMILY }}>
                  {t('results.sidebar.noMatch', { query: selectorSearch.trim() })}
                </Typography>
              </MenuItem>
            )}
            {filteredSimulationResults.map((sim) => {
              const tripCount = dropdownTripCount(sim);
              return (
              <MenuItem key={sim.run_id} value={sim.run_id}>
                <Box>
                  <Typography variant="body2" sx={{ color: BRAND.tarmacBlack, fontWeight: 500, fontFamily: FONT_FAMILY }}>
                    {sim.scenario_name || sim.run_id}
                    {(tripCount != null || sim.success_ratio != null) && (
                      <span style={{ fontWeight: 400, opacity: 0.55, marginLeft: 8, fontSize: '0.9em' }}>
                        {[
                          tripCount != null && t('results.sidebar.tripCount', { count: tripCount }),
                          sim.success_ratio != null && t('results.sidebar.successPercent', { percent: (sim.success_ratio * 100).toFixed(1) }),
                        ].filter(Boolean).join(' | ')}
                      </span>
                    )}
                  </Typography>
                  {sim.scenario_name && (
                    <Typography variant="caption" sx={{ color: BRAND.labelGray, display: 'block', fontFamily: FONT_FAMILY }}>
                      {sim.run_id}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            )})}
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            onClick={handleReload}
            disabled={loadingResults}
            size="small"
            sx={{ color: SIDEBAR.value }}
            title={t('results.sidebar.reloadResults')}
          >
            {loadingResults ? (
              <CircularProgress size={20} sx={{ color: BRAND.primaryOrange }} />
            ) : (
              <Icon name="arrows-rotate" size={20} />
            )}
          </IconButton>
          {onShare && selectedSim && (
            <Tooltip title={t('results.sidebar.manageAccess')} placement="top" arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={onShare}
                  sx={{ color: SIDEBAR.value }}
                >
                  <Icon d={ICON_FALLBACKS.Share} size={20} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<Icon size={20} name="arrow-left" />}
            onClick={handleBackToMap}
            fullWidth
            sx={{
              color: SIDEBAR.value,
              borderColor: SIDEBAR.border,
              fontFamily: FONT_FAMILY,
              '&:hover': { borderColor: BRAND.primaryOrange, bgcolor: 'rgba(238, 115, 52, 0.2)' },
            }}
          >
            {t('results.sidebar.backToMap')}
          </Button>
        </Box>
      </Box>

      {showInsights && (
        <Box sx={{ px: 3, pt: 2, pb: 0 }}>
          <Section title={t('results.sidebar.liftangoInsights')} last={true}>
            <LiftangoInsightsCarousel />
          </Section>
        </Box>
      )}

      {/* Metrics content */}
      <Box sx={{ px: 3, pt: 2, pb: 1 }}>
        {!selectedSim ? (
          <Typography variant="body2" sx={{ color: SIDEBAR.label, fontFamily: FONT_FAMILY }}>
            {t('results.sidebar.selectRunPrompt')}
          </Typography>
        ) : (
          <>
            {/* Section A: Hero metrics (3 columns) */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: 1,
                mb: 3,
                pb: 2,
                borderBottom: `1px solid ${SIDEBAR.border}`,
              }}
            >
              <HeroCell
                icon={<Icon size={20} name="circle-check" />}
                value={formatMetric(raw('success_ratio'), 'percent')}
                label={t('results.sidebar.heroSuccessRate')}
                iconColor={BRAND.successGreen}
                tooltip={METRIC_DEFINITIONS.successRate}
              />
              <HeroCell
                icon={<Icon size={20} name="users" />}
                value={formatMetric(raw('no_rides'), 'integer')}
                label={t('results.sidebar.heroTotalRequests')}
                iconColor={SIDEBAR.value}
              />
              <HeroCell
                icon={<Icon size={20} name="clock" />}
                value={
                  typeof raw('waiting_time_min_median') === 'number'
                    ? formatMetric(raw('waiting_time_min_median'), 'decimal') + ' ' + t('results.sidebar.minSuffix')
                    : '--'
                }
                label={t('results.sidebar.heroMedianWait')}
                iconColor={SIDEBAR.value}
              />
            </Box>

            {/* Section B: Detailed groups */}

            {/* Group 1: Rider Experience */}
            <Section title={t('results.sidebar.groupRiderExperience')} last={false}>
              <MetricRow
                label={t('results.sidebar.rowFirstQuartileWaitTime')}
                value={
                  typeof raw('waiting_time_min_first_quartile') === 'number'
                    ? formatMetric(raw('waiting_time_min_first_quartile'), 'decimal') + ' ' + t('results.sidebar.minSuffix')
                    : '--'
                }
                tooltip={METRIC_DEFINITIONS.waitTime}
                onGraphOpen={
                  waitDistribution
                    ? (e) =>
                        openMetricDetail(e.currentTarget, {
                          data: waitDistribution,
                          title: t('results.sidebar.boxPlotWaitTimeTitle'),
                          unit: t('results.sidebar.minSuffix'),
                        })
                    : undefined
                }
              />
              <MetricRow
                label={t('results.sidebar.rowMedianTripTime')}
                value={
                  typeof raw('median_time_on_vehicle') === 'number'
                    ? formatMetric(raw('median_time_on_vehicle'), 'decimal') + ' ' + t('results.sidebar.minSuffix')
                    : '--'
                }
              />
              <MetricRow
                label={t('results.sidebar.rowMaxTripTime')}
                value={
                  typeof raw('max_time_on_vehicle') === 'number'
                    ? formatMetric(raw('max_time_on_vehicle'), 'decimal') + ' ' + t('results.sidebar.minSuffix')
                    : '--'
                }
              />
              <MetricRow
                label={t('results.sidebar.rowMedianTripDistance')}
                value={
                  typeof raw('trip_distance_median') === 'number'
                    ? formatMetric(raw('trip_distance_median'), 'decimal') + ' ' + t('results.sidebar.kmSuffix')
                    : '--'
                }
                onGraphOpen={
                  tripDistanceDistribution
                    ? (e) =>
                        openMetricDetail(e.currentTarget, {
                          data: tripDistanceDistribution,
                          title: t('results.sidebar.boxPlotTripDistanceTitle'),
                          unit: t('results.sidebar.kmSuffix'),
                        })
                    : undefined
                }
              />
            </Section>

            {/* Group 2: Fleet Efficiency */}
            <Section title={t('results.sidebar.groupFleetEfficiency')} last={false}>
              <MetricRow
                label={t('results.sidebar.rowUtilization')}
                value={formatMetric(raw('passenger_per_vehicle_hour'), 'decimal')}
                tooltip={METRIC_DEFINITIONS.utilization}
              />
              <MetricRow
                label={t('results.sidebar.rowAvgVehicleHours')}
                value={
                  (() => {
                    const totalHrs = num(raw('vehicle_hours'));
                    const totalVehicles = num(raw('no_vehicles')) || num(raw('allocated_vehicle_shifts'));
                    if (totalVehicles <= 0) return '--';
                    return formatMetric(totalHrs / totalVehicles, 'decimal') + ' ' + t('results.sidebar.hrsSuffix');
                  })()
                }
                tooltip={METRIC_DEFINITIONS.avgVehicleHours}
              />
              <MetricRow
                label={t('results.sidebar.rowAverageVehicleDistance')}
                value={
                  (() => {
                    const total = num(raw('total_km_travelled'));
                    const allocated = num(raw('allocated_vehicle_shifts'));
                    if (allocated <= 0) return '--';
                    return formatMetric(total / allocated, 'decimal') + ' ' + t('results.sidebar.kmSuffix');
                  })()
                }
              />
              <MetricRow
                label={t('results.sidebar.rowActiveIdleKm')}
                value={`${formatMetric(raw('active_km'), 'decimal')} / ${formatMetric(raw('dead_running_km'), 'decimal')}`}
                tooltip={
                  <Box sx={{ '& > :not(:last-child)': { mb: 1 } }}>
                    <Typography component="span" sx={{ fontSize: '0.8125rem', lineHeight: 1.5, maxWidth: 320, display: 'block', fontFamily: FONT_FAMILY }}>
                      {METRIC_DEFINITIONS.activeKm}
                    </Typography>
                    <Typography component="span" sx={{ fontSize: '0.8125rem', lineHeight: 1.5, maxWidth: 320, display: 'block', fontFamily: FONT_FAMILY }}>
                      {METRIC_DEFINITIONS.idleKm}
                    </Typography>
                  </Box>
                }
              />
            </Section>

            {/* Group 3: Coverage */}
            <Section title={t('results.sidebar.groupCoverage')} last={false}>
              <MetricRow
                label={t('results.sidebar.rowSharedTripRate')}
                value={formatMetric(raw('pcnt_shared_rides'), 'percent')}
                tooltip={METRIC_DEFINITIONS.sharedTripRate}
                valueColor={BRAND.teal}
              />
              <MetricRow label={t('results.sidebar.rowAllocatedVehicles')} value={formatMetric(raw('allocated_vehicle_shifts'), 'integer')} />
              <MetricRow
                label={t('results.sidebar.rowMaxGroups')}
                value={formatMetric(raw('max_pax'), 'integer')}
              />
            </Section>

          </>
        )}
      </Box>
      {/* End scrollable content area */}
      </Box>

      {/* Footer: settings button pinned to bottom-right */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1.5,
          py: 1,
          borderTop: `1px solid ${SIDEBAR.border}`,
          gap: 1,
        }}
      >
        <Button
          size="small"
          variant="contained"
          startIcon={<Icon d={ICON_FALLBACKS.AddLocationAlt} size={20} />}
          onClick={(event) => setAddToMapMenuAnchorEl(event.currentTarget)}
          disabled={addToMapDisabled || addToMapBusy}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          {addToMapBusy ? t('results.sidebar.importing') : t('results.sidebar.addToMap')}
        </Button>
        <Menu
          anchorEl={addToMapMenuAnchorEl}
          open={Boolean(addToMapMenuAnchorEl)}
          onClose={() => setAddToMapMenuAnchorEl(null)}
        >
          <MenuItem
            onClick={() => {
              setAddToMapMenuAnchorEl(null);
              onAddToMapOnDemand?.();
            }}
            disabled={addToMapOnDemandDisabled || addToMapBusy}
          >
            {t('results.sidebar.onDemand')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAddToMapMenuAnchorEl(null);
              onAddToMapFixedRoute?.();
            }}
            disabled={addToMapFixedRouteDisabled || addToMapBusy}
          >
            {t('results.sidebar.fixedRoute')}
          </MenuItem>
        </Menu>
        <Tooltip title={t('results.sidebar.viewSimulationSettings')} placement="top" arrow>
          <span>
            <IconButton
              size="small"
              disabled={!selectedSim}
              onClick={() => {
                trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.simulation_results_modal_opened, {
                  modal: 'settings',
                  timestamp: new Date().toISOString(),
                });
                setSettingsOpen(true);
              }}
              sx={{
                color: SIDEBAR.label,
                '&:hover': { color: SIDEBAR.value, bgcolor: 'rgba(0,0,0,0.04)' },
                '&.Mui-disabled': { color: 'rgba(0,0,0,0.15)' },
              }}
            >
              <Icon name="gear" size={20} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>

    <Popover
      open={metricDetail != null}
      anchorEl={metricDetail?.anchor ?? null}
      onClose={closeMetricDetail}
      anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
      transformOrigin={{ vertical: 'center', horizontal: 'left' }}
      disableRestoreFocus
      slotProps={{
        paper: {
          sx: {
            ml: 1,
            p: 2,
            maxWidth: 420,
            bgcolor: liftangoBrandTokens.simulationResults.tooltipBackground,
            border: `1px solid ${BRAND.borderLight}`,
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
          },
        },
      }}
    >
      {metricDetail ? (
        <MetricsDistributionChart title={metricDetail.title} unit={metricDetail.unit} data={metricDetail.data} />
      ) : null}
    </Popover>

    <SimulationSettingsModal
      open={settingsOpen}
      resultsId={selectedSim?.results_id ?? selectedSim?.run_id}
      onClose={() => setSettingsOpen(false)}
    />
    </>
  );
};

export default SimulationResultsSidebar;

